import { SimpleGrid } from '@uk-source-web/simple-grid';
import React, { useEffect, useRef, useState } from 'react';
import {
  ContainerWrapper,
  DiscountContainer,
  DiscountHelperTextWrapper,
  DiscountWrapper,
  ParagraphWrapper,
} from './BasketDiscount.styles';
import { Paragraph } from '@uk-source-web/paragraph';
import { TextInputWithLabel, TextInputWithLabelProps } from '@uk-source-web/text-input-with-label';
import { Button } from '@uk-source-web/button';
import { LinkWithIcon } from '@uk-source-web/link-with-icon';
import { Spacer } from '@/components/Global/RegisterAccount/RegisterAccount.styles';
import { Link } from '@uk-source-web/link';
import BasketMoreInformationContent from './BasketMoreInformationContent/BasketMoreInformationContent';
import {
  AppDispatch,
  AppState,
  UserBasketItem,
  applyDiscount,
  clearDiscount,
  updateUserBasket,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { ProductBasketDiscount } from '@/services/discount.services';
import { getCurrencySymbol } from '@/helpers/findPrices';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { t } from 'i18next';
import handleTranslation from '@/helpers/handleTranslation';

const BasketDiscount = ({
  shouldShowDiscountInput = true,
  basketSummary,
}: {
  shouldShowDiscountInput: boolean;
  basketSummary: {};
}) => {
  type TypedDispatch = ThunkDispatch<AppState, unknown, any>;
  const dispatch: TypedDispatch = useDispatch();
  const [discountCode, setDiscountCode] = useState<string>('');

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [state, setState] = useState<TextInputWithLabelProps['state']>(undefined);

  const { discountData, loading, error } = useAppSelector(state => state.userDiscount);

  const { user } = useAppSelector(({ userSession }) => userSession);
  const { userBasket } = useAppSelector(({ userBasket: { data } }) => ({
    userBasket: data,
  }));

  const prevBasketRef = useRef<UserBasketItem[]>([]);
  const totalItemsWithDiscount = discountData?.itemDiscount
    .filter(item => item.productRecurringDiscount > 0 || item.productUpfrontDiscount > 0)
    .reduce((sum, item) => sum + item.numberOfItems, 0);

  const totalItemDiscountAmount = (
    discountData?.subtotal && discountData?.dueNow ? discountData?.subtotal - discountData?.dueNow : 0
  ).toFixed(2);

  const discountedProducts =
    discountData?.itemDiscount?.filter(
      ({ productUpfrontDiscount, productRecurringDiscount }) =>
        productUpfrontDiscount !== 0 || productRecurringDiscount !== 0
    ) || [];

  const discountedProductIds = discountedProducts.map(({ productId }) => productId);

  // handle basket change
  useEffect(() => {
    const prevBasket = prevBasketRef.current || [];
    const getBasketData = (items: UserBasketItem[]) =>
      (items || []).map(item => ({
        productId: item.productId,
        quantity: item.quantity,
      }));

    const prevBasketData = getBasketData(prevBasket);
    const currentBasketData = getBasketData(userBasket?.items);

    let productIdChange: string | null = null;

    const hasQuantityChanged = currentBasketData.some(currentItem => {
      productIdChange = currentItem.productId;

      const prevItem = prevBasketData.find(p => p.productId === currentItem.productId);
      return prevItem ? prevItem.quantity !== currentItem.quantity : true;
    });

    if (hasQuantityChanged && discountedProductIds.includes(productIdChange || '')) {
      applyDiscountToBasket(discountData?.discountName);
    }

    prevBasketRef.current = userBasket?.items || [];
  }, [userBasket.items]);

  const sessionPrices = sessionStorage.getItem('originalBasketPrices');
  const parsedSessionPrices = sessionPrices ? JSON.parse(sessionPrices) : {};

  const applyDiscountToBasket = async (discountCode: any) => {
    type ProductType = (typeof products)[number];

    const products = userBasket.items.map(item => ({
      id: item.productId,
      price: parsedSessionPrices[item.productId] || item?.dxlInformation?.price[0]?.price ,
      numberOfUnits: item?.quantity ?? 1,
      pricingType: item?.dxlInformation?.pricingType || 'MONTHLY',
    })) as ProductBasketDiscount[];
    try {
      let resp = await dispatch(
        applyDiscount({
          productsDiscount: { discountCode, products },
          csrfToken: user?.csrfToken || '',
        })
      );
      if (resp.meta.requestStatus === 'fulfilled') {
        // Create a map of products by lookup
        const productsMap = products.reduce<Record<string, ProductType>>((map, product) => {
          map[String(product.id)] = product;
          return map;
        }, {} as Record<string, ProductType>);

        // Process each item discount and match with products
        resp.payload?.itemDiscount.forEach(
          (itemDisc: { productId: string; productDueNow: number; productRecurringCharges: number }) => {
            const productId = itemDisc.productId;

            productsMap[productId].price = itemDisc.productRecurringCharges;
            productsMap[productId].DueNow = itemDisc.productDueNow;
          }
        );

        const updatedItems = userBasket.items.map(item => {
          const updatedProduct = productsMap[item.productId];

          if (updatedProduct) {
            // Return the item with updated price
            return {
              ...item,
              dxlInformation: {
                ...item.dxlInformation,
                price: [
                  {
                    ...item.dxlInformation?.price[0],
                    price: updatedProduct.price,
                    DueNow: updatedProduct.DueNow,
                  },
                ],
              },
            };
          }

          // return unchanged item if no update is found
          return item;
        });

        updatedItems.forEach(item => {
          dispatch(updateUserBasket(item));
        });

        setState(undefined);
      }
    } catch (error: any) {
      console.log('error', error);
    }
  };
  useEffect(() => {
    if (error) {
      setErrorMessage(error.message);
      setState('error');
    }
  }, [error]);

  const handleValidateInput = (value: string) => {
    if (value.trim() === '') {
      return handleTranslation('Discount code cannot be empty.', 'Código de desconto não pode estar vazio');
    } else if (value.length < 5) {
      return handleTranslation(
        'Discount code must have at least 5 characters.',
        'Código de desconto deve conter ao menos 5 caracteres'
      );
    }
    return '';
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmedDiscountValue = e.target.value.trim();
    setDiscountCode(trimmedDiscountValue);

    const validationError = handleValidateInput(trimmedDiscountValue);

    if (validationError) {
      setErrorMessage(validationError);
      setState('error');
    } else {
      setErrorMessage('');

      // If the previous error was from the API, reset it
      if (error?.statusCode === 400 || error?.statusCode === 401) {
        setErrorMessage('');
      }

      setState('success');
    }
  };
  const handleDiscountCode = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    window.utag?.view({
      event_name: 'ui_interaction',
      component_name: 'Click Component',
      event_category: 'Apply a Discount Code',
      event_label: 'Apply',
      event_action: 'Apply a Discount Code',
      component_type: 'Button',
      discount_code: discountCode,
    });
    await applyDiscountToBasket(discountCode);
    setDiscountCode('');
  };

  const handleIconClick = () => {
    dispatch(clearDiscount());
    setDiscountCode('');
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleValidateInput(e.target.value);
  };

  const codePlaceholder = handleTranslation('Enter Code', 'Código de desconto');
  const andText = t('and');
  const acceptTermsText = handleTranslation(
    'By proceeding and clicking on “Continue” you are accepting the',
    'Ao clicar em "Completar compra" está a aceitar os'
  );

  const appliedTo = handleTranslation('Applied to', 'Aplicado à');
  const itemText = handleTranslation('item', 'produto');

  let linkToTermsAndConditions =
    'https://n.vodafone.ie/content/dam/gowingmyself/pdfs/vbu-pdfs-2023/Vodafone%20Business%20Marketplace%20Terms-%20as%20of%20February%202023.pdf';

  const termsAndConditionsEvent = (href: string) => {
    window.utag?.view({
      event_name: 'ui_interaction',
      component_name: 'Click Component',
      event_category: 'Accept Terms and Conditions',
      event_label: 'Terms and Conditions',
      event_action: 'Accept Terms and Conditions',
      component_type: 'Link',
    });
    window.location.href = href;
  };

  return (
    <SimpleGrid
      columns={{
        sm: 1,
        md: 1,
      }}
      verticalAlign="stretch"
      justify="center"
    >
      <ContainerWrapper>
        {shouldShowDiscountInput && (
          <>
            <DiscountWrapper errorMessage={errorMessage.length}>
              <TextInputWithLabel
                id="defaultTextInput"
                state={state}
                fieldWrapper={{
                  label: t('applyDiscountLabel'),
                  width: 'full',
                  stateText: errorMessage,
                }}
                textInput={{
                  id: 'discnout',
                  type: 'text',
                  placeholder: codePlaceholder,
                  value: discountCode,
                  onChange: handleDiscountChange,
                  onBlur: handleInputBlur,
                }}
              />
              <Button
                text={t('apply')}
                appearance="alt1"
                width="auto"
                onClick={handleDiscountCode}
                loading={loading}
                state={discountCode === '' || state === 'error' || state === 'warn' ? 'disabled' : undefined}
              />
            </DiscountWrapper>
            {discountData && !loading && (
              <DiscountHelperTextWrapper>
                <div>
                  <Paragraph size={1} weight={2}>
                    {discountData?.discountName}
                  </Paragraph>
                  <Paragraph size={1} weight={2}>
                    {appliedTo} {totalItemsWithDiscount} {itemText}
                    {totalItemsWithDiscount !== 1 ? 's' : ''} - {getCurrencySymbol()}
                    {totalItemDiscountAmount}
                  </Paragraph>
                </div>
                <LinkWithIcon
                  appearance="secondary"
                  text=""
                  icon={{
                    name: 'close',
                    group: 'system',
                  }}
                  onClick={handleIconClick}
                  srText="Screen reader text!"
                />
              </DiscountHelperTextWrapper>
            )}
          </>
        )}
      </ContainerWrapper>
      <DiscountContainer>
        <BasketMoreInformationContent basketSummary={basketSummary} />
        <Spacer h={30} />
        {shouldShowDiscountInput && (
          <>
            <ParagraphWrapper>
              <Paragraph size={1} weight={1}>
                {' '}
                {acceptTermsText}&nbsp;
              </Paragraph>
              <Link
                htmlAttributes={{ target: '_blank' }}
                onClick={() => {
                  termsAndConditionsEvent(linkToTermsAndConditions);
                }}
                dataAttributes={{ 'data-testid': 'terms-and-conditions-link' }}
                id="terms-and-conditions-link"
                text={t('termsAndConditions')}
                showUnderline={true}
                size={1}
              />
              <Paragraph size={1} weight={1}>
                &nbsp;{andText}&nbsp;
              </Paragraph>
              <Link
                htmlAttributes={{ target: '_blank' }}
                href="https://n.vodafone.ie/privacy.html"
                text={t('privacyPolicy')}
                showUnderline={true}
                size={1}
              />
            </ParagraphWrapper>
            <Spacer h={20} />
          </>
        )}
      </DiscountContainer>
    </SimpleGrid>
  );
};

export default BasketDiscount;
